import type { ReactElement } from 'react';
import { styled } from '@mui/material/styles';
import { Tab } from '@mui/material';
import dayjs from 'dayjs';
import { UseFormReturn } from 'react-hook-form';
import { v4 as uuidv4 } from 'uuid';

import { FileWrapper } from 'helpers/classes';
import { handleImageQualityThresholds } from 'helpers/image-quality-thresholds';

export type TTabs = 'detail' | 'close' | 'movements' | 'documentation';
export type TChangeTabs =
  | 'nameDetail'
  | 'homeWork'
  | 'approvals'
  | 'approvalsVote'
  | 'lavelRisk';

type Fields = {
  description: string;
  fileName: string;
  fileBase64: string;
  size: number;
};

interface ITabs {
  label: string;
  icon: ReactElement;
  component: ReactElement;
  type?: string;
  disabled?: boolean;
}

export const formatImagesAllow: string[] = ['.jpg', '.jpeg', '.png'];

export const formatDate = (date: string | Date) => {
  // Obtener la zona horaria del usuario solo una vez
  const userTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;

  return date
    ? dayjs.utc(date).local().tz(userTimeZone).format('DD/MM/YYYY HH:mm:ss')
    : '-----';
};

export const DetailTicketTab = styled(Tab)(() => ({
  '&.MuiTab-root': {
    height: '43px',
    minHeight: '43px',
    minWidth: '210px',
    padding: '9px',
    borderWidth: '1px',
    borderStyle: 'solid',
    borderColor: '#9C9C9C',
    borderRadius: '5px',
    margin: '0px 5px 5px 5px',
    justifyContent: 'start',
    '@media (max-width: 450px)': {
      height: '33px',
      minHeight: '33px',
      minWidth: '180px',
      paddingX: '5px',
      width: '100%'
    }
  },
  '&.Mui-selected': {
    borderColor: '#1976d2'
  }
}));

const rotateImage = (
  rawBase64: string,
  file: FileWrapper,
  size: number,
  form: UseFormReturn<Fields>
) => {
  const image = new Image();
  image.src = rawBase64;
  image.onload = () => {
    if (image.height > image.width) {
      const canvas = document.createElement('canvas');
      const context = canvas.getContext('2d');
      canvas.width = image.height;
      canvas.height = image.width;

      // Rotate in anticlockwise direction
      context.rotate((-90 * Math.PI) / 180);
      context.translate(-canvas.height, 0);

      context.drawImage(image, 0, 0);

      const newRawBase64 = canvas.toDataURL('image/jpeg', 0.95);

      // eslint-disable-next-line no-param-reassign
      rawBase64 = newRawBase64;
    }
    const commaIndex = rawBase64.indexOf(',');
    const base64Data = rawBase64.slice(commaIndex + 1);
    form.setValue('fileBase64', base64Data);
    form.setValue('fileName', file.file.name);
    form.setValue('size', size);
  };
};

export function settingFiles(
  form: UseFormReturn<Fields>,
  callbackError: (key: string, time?: number) => void
) {
  const setFile = async (files: FileWrapper[]) => {
    if (files.length > 0) {
      const file = files[0];
      const base64 = await file.getBase64();
      const size = file.getMbSize();
      const fileType = file?.file?.type.split('/')[0];
      const fileExtension = file?.file?.name.split('.').pop()?.toLowerCase();
      const validateIdImageAccepted = formatImagesAllow.includes(
        `.${fileExtension?.toLowerCase()}` || ''
      );

      if (fileType === 'image' && !validateIdImageAccepted) {
        // Image validation failed
        callbackError('ticket.ticketDetail.invalidateFormat');
      } else if (size > 10) {
        // Size validation failed
        callbackError('ticket.ticketDetail.sizeExceeded');
      } else {
        // All validations passed
        if (fileType === 'image') {
          const base64string = base64.toString();

          const handleImageAccepted = (isAccepted: boolean) => {
            if (isAccepted) rotateImage(base64string, file, size, form);
            else {
              callbackError('ticket.ticketDetail.invalidateImage', 2500);
              form.setValue('fileBase64', '');
              form.setValue('fileName', '');
              form.setValue('size', null);
            }
          };
          handleImageQualityThresholds(base64string, handleImageAccepted);
          return;
        }
        const newBase64 = base64
          .toString()
          .replace(`data:${file.file.type};base64,`, '');
        form.setValue('fileBase64', newBase64);
        form.setValue('fileName', file.file.name);
        form.setValue('size', size);
      }
    } else {
      // No file selected
      callbackError('ticket.ticketDetail.noFileSelected');
    }
  };
  return { setFile };
}

export const StyledPre = styled('pre')(() => ({
  backgroundColor: '#fff',
  border: '1px solid rgba(118, 118, 118, 0.3)',
  borderRadius: '4px',
  padding: '0 1rem',
  whiteSpace: ' pre-wrap',
  overflow: 'auto',
  fontFamily: 'Roboto',
  fontSize: '14px'
}));

const Campo = styled('span')(() => ({
  fontWeight: '500',
  color: '#0E335C'
}));

export const renderFormattedText = (text: string) =>
  text?.split('\n').map((line) => {
    const parts = line.split(/:(.+)/);
    if (parts?.length > 1) {
      return (
        <p key={parts[0]}>
          <Campo>{parts[0]}:&nbsp;</Campo>
          {parts[1].trim()}
        </p>
      );
    }
    return <p key={parts[0]}>{line}</p>;
  });

export const getTab = (tab: TTabs | number, tabs: ITabs[]) => {
  const index = tabs.findIndex((item) => item?.type === tab);
  if (typeof tab === 'number') return tab;
  if (index && index !== -1) return index;
  return 0;
};

export const getTabChange = (tab: TChangeTabs | number, tabs: ITabs[]) => {
  const index = tabs.findIndex((item) => item?.type === tab);
  if (typeof tab === 'number') return tab;
  if (index && index !== -1) return index;
  return 0;
};

export const formatClosureComment = (message: string) => {
  const lines = message.split('\n').filter((line) => line.trim() !== '');

  return lines.map((line) => {
    const match = line.match(/^([^:]+):\s(.+)$/);
    if (match) {
      const key = match[1].trim();
      const value = match[2].trim();
      return (
        <pre
          key={uuidv4()}
          style={{
            whiteSpace: 'pre-wrap',
            wordWrap: 'break-word',
            overflowWrap: 'break-word'
          }}
        >
          <strong>{key}: </strong>
          {value}
        </pre>
      );
    }
    return <pre key={uuidv4()}>{line}</pre>;
  });
};
